/**
 * DemoWidget - Magic Button für Demo-Szenarien
 *
 * Zeigt ein kleines Floating Widget unten links,
 * das Demo-Aktionen ermöglicht (Reset, Morning, History).
 *
 * SCENARIO_MORNING:
 * - Employees vom Server laden
 * - Lokale DB befüllen (TimeSheet + RUNNING Entries)
 * - Rein Frontend-seitig
 *
 * Nur sichtbar wenn VITE_ENABLE_DEMO_MODE=true
 */

import { signal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import { Wand2, RotateCcw, Sun, Calendar, X, Loader2 } from 'lucide-preact';
import { DateTime } from 'luxon';
import { createId } from '@paralleldrive/cuid2';
import { db, clearDatabase, setCachedEmployees } from '../../data/db';
import { fetchEmployees } from '../../api/client';
import { SEED_IDS } from '@timetrack/db';
import type { TimeSheet, TimeEntry, Employee } from '@timetrack/domain';
import styles from './DemoWidget.module.css';

// =============================================================================
// State
// =============================================================================

const isOpen = signal(false);
const isLoading = signal(false);
const lastResult = signal<{ success: boolean; message: string } | null>(null);

// =============================================================================
// Actions
// =============================================================================

/**
 * SCENARIO_MORNING: Team arbeitet seit 07:00
 *
 * 1. Employees aus lokaler Dexie-DB laden
 * 2. TimeSheets/Entries löschen (Employees behalten!)
 * 3. TimeSheet für heute erstellen
 * 4. RUNNING Entries für Team-Mitarbeiter erstellen
 */
async function handleMorning(): Promise<void> {
  if (isLoading.value) return;

  isLoading.value = true;
  lastResult.value = null;

  try {
    // 1. Employees aus lokaler DB laden
    const allEmployees = await db.employee.toArray();

    if (allEmployees.length === 0) {
      throw new Error('Keine Mitarbeiter in lokaler DB. Bitte erst App normal starten.');
    }

    // Team-Mitarbeiter filtern (die dem Demo-Foreman zugeordnet sind)
    const teamEmployees = allEmployees.filter(
      (emp) => emp.foremanId === SEED_IDS.DEMO_FOREMAN && emp.active
    );

    if (teamEmployees.length === 0) {
      throw new Error('Keine Team-Mitarbeiter gefunden (foremanId mismatch?)');
    }

    const now = DateTime.now();
    const nowUtc = now.toUTC().toISO() as string;
    const today = now.toFormat('yyyy-MM-dd');

    // 2. Nur TimeSheets und Entries löschen (Employees behalten!)
    await db.timeEntry.clear();
    await db.timeSheet.clear();
    await db.pendingOp.clear();

    // 3. TimeSheet für heute erstellen
    const sheetId = createId();
    const sheet: TimeSheet = {
      id: sheetId,
      tenantId: SEED_IDS.DEMO_TENANT,
      date: today,
      foremanId: SEED_IDS.DEMO_FOREMAN,
      supervisorId: SEED_IDS.DEMO_SUPERVISOR,
      costCenterId: SEED_IDS.DEMO_COST_CENTER,
      status: 'DRAFT',
      taLocked: false,
      createdAt: nowUtc,
      updatedAt: nowUtc,
      version: 1,
    };
    await db.timeSheet.add(sheet);

    // 4. RUNNING Entries für Team-Mitarbeiter (Start 07:00 lokal -> UTC)
    const startTime = now.set({ hour: 7, minute: 0, second: 0, millisecond: 0 }).toUTC().toISO() as string;

    const entries: TimeEntry[] = teamEmployees.map((emp) => ({
      id: createId(),
      timeSheetId: sheetId,
      employeeId: emp.id,
      personnelNumber: emp.personnelNumber,
      employeeName: `${emp.firstName} ${emp.lastName}`,
      type: 'WORK',
      status: 'RUNNING',
      startTime,
      endTime: null,
      breakMinutes: 0,
      createdAt: nowUtc,
      updatedAt: nowUtc,
      version: 1,
    }));
    await db.timeEntry.bulkAdd(entries);

    console.info(`[Demo] Morning Scenario: ${entries.length} Entries mit RUNNING Status erstellt`);

    lastResult.value = {
      success: true,
      message: `${entries.length} Mitarbeiter arbeiten seit 07:00`,
    };

    // Kurze Verzögerung, dann Reload
    setTimeout(() => {
      window.location.href = '/today';
    }, 300);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
    lastResult.value = {
      success: false,
      message: `Fehler: ${message}`,
    };
    console.error('[Demo] Morning Scenario fehlgeschlagen:', error);
    isLoading.value = false;
  }
}

/**
 * RESET: Alle lokalen Daten löschen
 */
async function handleReset(): Promise<void> {
  if (isLoading.value) return;

  isLoading.value = true;
  lastResult.value = null;

  try {
    // Lokale DB löschen
    await clearDatabase();

    // LocalStorage-Cache löschen
    localStorage.removeItem('timetrack:employees');
    localStorage.removeItem('timetrack:tenantConfig');

    // Optional: Auch Server-DB löschen
    try {
      await fetch('/api/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'RESET' }),
      });
    } catch {
      // Server nicht erreichbar - kein Problem für lokalen Reset
      console.warn('[Demo] Server-Reset fehlgeschlagen (offline?)');
    }

    lastResult.value = {
      success: true,
      message: 'Alle Daten gelöscht',
    };

    console.info('[Demo] Reset durchgeführt');

    setTimeout(() => {
      window.location.href = '/today';
    }, 300);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
    lastResult.value = {
      success: false,
      message: `Fehler: ${message}`,
    };
    console.error('[Demo] Reset fehlgeschlagen:', error);
    isLoading.value = false;
  }
}

/**
 * SCENARIO_HISTORY: 7 Tage Historie generieren
 *
 * Geht an die API (für Admin-Dashboard relevant)
 */
async function handleHistory(): Promise<void> {
  if (isLoading.value) return;

  isLoading.value = true;
  lastResult.value = null;

  try {
    const response = await fetch('/api/demo', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'SCENARIO_HISTORY' }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();

    // Employees vom Server laden und in Dexie cachen
    const serverEmployees = await fetchEmployees();
    if (serverEmployees !== null) {
      const employees: Employee[] = serverEmployees.map((emp) => ({
        id: emp.id,
        tenantId: SEED_IDS.DEMO_TENANT,
        personnelNumber: emp.personnelNumber,
        firstName: emp.firstName,
        lastName: emp.lastName,
        foremanId: emp.foremanId ?? undefined,
        active: emp.active,
        createdAt: emp.createdAt,
        updatedAt: emp.updatedAt,
      }));
      await setCachedEmployees(employees);
      console.info(`[Demo] ${employees.length} Employees in Dexie gecacht`);
    }

    lastResult.value = {
      success: true,
      message: result.message || '7 Tage Historie erstellt',
    };

    console.info('[Demo] History Scenario:', result);

    // Für History: Redirect zu Admin statt /today
    setTimeout(() => {
      window.location.href = '/admin';
    }, 300);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unbekannter Fehler';
    lastResult.value = {
      success: false,
      message: `Fehler: ${message}`,
    };
    console.error('[Demo] History Scenario fehlgeschlagen:', error);
    isLoading.value = false;
  }
}

function toggleOpen(): void {
  isOpen.value = !isOpen.value;
  lastResult.value = null;
}

// =============================================================================
// Component
// =============================================================================

export function DemoWidget() {
  // Guard: Demo-Mode muss aktiviert sein
  const demoEnabled = import.meta.env.VITE_ENABLE_DEMO_MODE === 'true';
  if (!demoEnabled) {
    return null;
  }

  const widgetRef = useRef<HTMLDivElement>(null);

  // Click outside to close
  useEffect(() => {
    if (!isOpen.value) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (widgetRef.current && !widgetRef.current.contains(e.target as Node)) {
        isOpen.value = false;
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen.value]);

  return (
    <div ref={widgetRef} className={styles.widget}>
      {/* Floating Button */}
      <button
        className={styles.trigger}
        onClick={toggleOpen}
        aria-label="Demo-Menü öffnen"
        title="Demo-Aktionen"
      >
        {isOpen.value ? <X size={20} /> : <Wand2 size={20} />}
      </button>

      {/* Popover Menu */}
      {isOpen.value && (
        <div className={styles.popover}>
          <div className={styles.header}>
            <Wand2 size={16} />
            <span>Demo-Aktionen</span>
          </div>

          {/* Result Message */}
          {lastResult.value && (
            <div
              className={`${styles.result} ${lastResult.value.success ? styles.success : styles.error}`}
            >
              {lastResult.value.message}
            </div>
          )}

          {/* Action Buttons */}
          <div className={styles.actions}>
            <button
              className={styles.actionBtn}
              onClick={() => void handleMorning()}
              disabled={isLoading.value}
            >
              {isLoading.value ? (
                <Loader2 size={16} className={styles.spinner} />
              ) : (
                <Sun size={16} />
              )}
              <span>Start Morning</span>
              <span className={styles.hint}>Team arbeitet seit 07:00</span>
            </button>

            <button
              className={styles.actionBtn}
              onClick={() => void handleHistory()}
              disabled={isLoading.value}
            >
              {isLoading.value ? (
                <Loader2 size={16} className={styles.spinner} />
              ) : (
                <Calendar size={16} />
              )}
              <span>Gen History</span>
              <span className={styles.hint}>7 Tage auf Server</span>
            </button>

            <button
              className={`${styles.actionBtn} ${styles.danger}`}
              onClick={() => void handleReset()}
              disabled={isLoading.value}
            >
              {isLoading.value ? (
                <Loader2 size={16} className={styles.spinner} />
              ) : (
                <RotateCcw size={16} />
              )}
              <span>Reset</span>
              <span className={styles.hint}>Lokal + Server löschen</span>
            </button>
          </div>

          <div className={styles.footer}>
            Demo-Mode aktiv
          </div>
        </div>
      )}
    </div>
  );
}
